import { compose, join, map, pathOr, split } from 'ramda';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { APP_CONSTANT } from 'src/constants';
import { AppContext } from 'src/context/AppContextProvider';
import { deleteAllCookie } from 'src/utils/common.util';
import { ReactComponent as AngleDown } from '../../assets/icons/angle-down.svg';
import useClickOutsideHandler from '../../hooks/useClickOutsideHandler.hook';
import { t } from '../../i18n';
import Routes from '../../modules/Navigator/Navigator.route';
import { RegisterButton, SignInButton } from './UserMenu.style';
import ConfirmationDialog from 'src/components/ConfirmationDialog/ConfirmationDialog';

interface IProps {
  cookies: {
    userInfo: {
      name: { nameFirst: string; nameLast: string };
    };
    role: string;
  };
}

const UserMenu: React.FC<IProps> = ({ cookies }) => {
  const mainMenuRef = useRef<any>(null);
  const [isConfirmedLeaving, setIsConfirmedLeaving] = useState<boolean>(false);
  const [showMainMenu, setShowMainMenu] = useState<boolean>(false);
  const [isLoggedInUser, setIsLoggedInUser] = useState<boolean>(false);
  const history = useHistory();
  const [, , removeCookie] = useCookies(['']);
  const context = useContext(AppContext);

  const handleClickOutside = () => {
    setShowMainMenu(false);
  };
  const handleShowMainMenu = () => {
    setShowMainMenu((prev) => !prev);
  };
  useClickOutsideHandler(mainMenuRef, handleClickOutside);

  const transformNameToShortText = compose(
    join(''),
    map((item) => item[0]),
    split(' ')
  );

  const goToSignIn = () => {
    history.push(Routes.SignIn);
  };

  const goToRegister = () => {
    history.push(Routes.Register);
  };

  const goToResetPassword = () => {
    history.push(Routes.ResetPassword);
  };

  const closeConfirmationPopup = () => setIsConfirmedLeaving(false);

  const logout = () => {
    // if the user dont have any dirty form
    // clear all cookie as well as intervalIds
    // then sign out them
    removeCookie('role');
    if ((window as any).intervalId1 || (window as any).intervalId2) {
      clearInterval((window as any).intervalId1);
      clearInterval((window as any).intervalId2);
    }
    deleteAllCookie();
    goToSignIn();
  };

  const handleClickSignOut = () => {
    const isFormDirty = Object.keys(context.formState).some(
      (f) => context.formState[f] === APP_CONSTANT.DIRTY
    );
    if (!isFormDirty) {
      return logout();
    }

    setIsConfirmedLeaving(true);
  };

  useEffect(() => {
    setIsLoggedInUser(
      [
        APP_CONSTANT.ROLE_SINGLE_USER,
        APP_CONSTANT.ROLE_LEGAL_GUARDIAN
      ].includes(cookies.role)
    );
  }, [cookies.role]);

  const renderUserMenu = () => {
    return (
      <>
        <ConfirmationDialog
          open={isConfirmedLeaving}
          confirmHandler={logout}
          cancelHandler={closeConfirmationPopup}
          textTitle={t('patientDocument.sendMessages.cancelPopup.title')}
          textDescription={t(
            'patientDocument.sendMessages.cancelPopup.description'
          )}
        />
        <div ref={mainMenuRef} className="navbar__user-container">
          <div onClick={handleShowMainMenu} className="navbar__user-wrapper">
            <div className="navbar__short-name-block">
              <p className="navbar__short-name-text">
                {transformNameToShortText(
                  `${pathOr('', ['userInfo', 'name', 'nameFirst'])(
                    cookies
                  )} ${pathOr('', ['userInfo', 'name', 'nameMiddle'])(
                    cookies
                  )} ${pathOr('', ['userInfo', 'name', 'nameLast'])(cookies)}`
                )}
              </p>
            </div>
            <p className="navbar__user-name">
              {`${pathOr('', ['userInfo', 'name', 'nameFirst'])(
                cookies
              )} ${pathOr('', ['userInfo', 'name', 'nameMiddle'])(
                cookies
              )} ${pathOr('', ['userInfo', 'name', 'nameLast'])(cookies)}`}
            </p>
            <div>
              <AngleDown />
            </div>
          </div>
          {showMainMenu && (
            <div className="navbar__main-menu-wrapper">
              <div className="navbar__main-menu">
                <p className="navbar__button-text" onClick={goToResetPassword}>
                  {t('navbar.menu.button.reset')}
                </p>
                <p className="navbar__button-text" onClick={handleClickSignOut}>
                  {t('navbar.menu.button.signOut')}
                </p>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderGuestButtons = () => {
    return (
      <div>
        <SignInButton onClick={goToSignIn}>
          {t('signIn.button.signIn')}
        </SignInButton>
        <RegisterButton
          variant="contained"
          color="primary"
          onClick={goToRegister}
        >
          {t('general.register')}
        </RegisterButton>
      </div>
    );
  };

  return isLoggedInUser ? renderUserMenu() : renderGuestButtons();
};

export default UserMenu;
